import { UseWaitForTransactionReceiptParameters, UseWaitForTransactionReceiptReturnType } from 'wagmi'
import { usePublicClient } from 'wagmi'
import { useQuery } from '@tanstack/react-query'
import { Hash } from 'viem'

import { useWaitForTransactionReceiptGnosisSafe } from './useWaitForTransactionReceiptGnosisSafe'
import { useWalletType } from './useWalletType'
import { waitForTxReceipt } from '@/utils/waitForTxReceipt'

/**
 * This will watch for tx receipt for both "normal" tx hashes and Gnosis Safe "subtransaction" hashes.
 * @note: Gnosis Safe tx can be sent as a subtx (just signed and later executed) or as a standalone tx
 * (signed and executed in one go). We have no way of knowing so we watch for both.
 *
 * Uses custom waitForTxReceipt utility that properly retries on receipt-not-found errors,
 * which wagmi's built-in hook doesn't handle.
 */
export function useWaitForTransactionReceiptUniversal(
  args: UseWaitForTransactionReceiptParameters = {},
): UseWaitForTransactionReceiptReturnType {
  const walletType = useWalletType()
  // Pass chainId to get the correct public client for the specified chain
  const publicClient = usePublicClient({ chainId: args.chainId })

  const gnosisTxReceipt = useWaitForTransactionReceiptGnosisSafe({
    ...args,
    hash: args.hash,
    query: {
      enabled: walletType === 'gnosis-safe',
    },
  })

  const shouldFetchReceipt = !!args.hash && gnosisTxReceipt.data === undefined && !!publicClient

  const queryKey = ['waitForTransactionReceipt', args.hash, args.chainId, args.confirmations] as const

  const receipt = useQuery({
    queryKey,
    queryFn: async () => {
      if (!args.hash || !publicClient) {
        throw new Error('Missing hash or publicClient')
      }
      return waitForTxReceipt(publicClient, args.hash as Hash, {
        confirmations: args.confirmations,
        pollingInterval: args.pollingInterval,
        onReplaced: args.onReplaced,
      })
    },
    enabled: shouldFetchReceipt,
    retry: false, // waitForTxReceipt handles its own retries
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 5 * 60 * 1000, // 5 minutes
  })

  if (gnosisTxReceipt.data) {
    return gnosisTxReceipt
  }

  // Map useQuery result to match UseWaitForTransactionReceiptReturnType
  return {
    ...receipt,
    queryKey,
  } as unknown as UseWaitForTransactionReceiptReturnType
}
